import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  ConjugateGraphQL,
  LinkedMoleculeGraphQL,
  SourceLinkedMoleculeConnectionSideGraphQL,
  ConjugateConnectionSideGraphQL,
  SourceLinkedMoleculeConnectionSideEntityGraphQL,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import Type from 'molbio/maps/Type';
import {FONT_PROXIMA_13, FONT_PROXIMA_14} from 'styles/FontConstants';
import {DEFAULT_TABLE_COLUMN_WIDTH} from 'ui-library/scrollable/ScrollableTable';

/* Cell height constants */
const CHIP_HEIGHT = 28;

// Space between each molecule in conjugate chain
const VERTICAL_MOLECULE_SPACING = 10;

// Space between different conjugate chains
const VERTICAL_CONJUGATE_SPACING = 34;

// Total of space at above first conjugate and below last conjugate
const TOTAL_VERTICAL_CELL_PADDING = 20;

/* Cell width constants */
const CHIP_ICON_WIDTH = 12;

const CHIP_WIDTH_TOTAL_PADDING = 20;

const DASH_WIDTH = 20;

// Total space around and between each element in conjugate description horiztonally
const TOTAL_HORIZONTAL_CELL_PADDING = 50;

const LEAVING_GROUP_PREFIX = 'LG';
const R_GROUP_PREFIX = 'R';

function getConjugateDisplayHeight(conjugate: ConjugateGraphQL): number {
  const numLinkedMolecules = conjugate.linkedMolecules ? conjugate.linkedMolecules.length : 0;

  // There is 1 additional entity for the molecule that is directly attached to the oligo
  const numEntities = numLinkedMolecules + 1;

  return (
    numEntities * CHIP_HEIGHT + (numEntities - 1) * VERTICAL_MOLECULE_SPACING + TOTAL_VERTICAL_CELL_PADDING
  );
}

export function getConjugatesDisplayHeight(conjugates: Array<ConjugateGraphQL>): number {
  const height = conjugates.reduce((height, conjugate) => height + getConjugateDisplayHeight(conjugate), 0);
  return height + (conjugates.length - 1) * VERTICAL_CONJUGATE_SPACING;
}

export function getEntityText(entity: SourceLinkedMoleculeConnectionSideEntityGraphQL): string {
  if (isInaccessible(entity)) {
    return 'No access';
  }
  // This case will cause an assertion error when rendering the conjugates display
  // since each linked molecule can only be chained onto another molecule.
  if (entity.__typename !== 'MoleculeNode') {
    return '';
  }
  return entity.name;
}

function isLinkedMolecule(
  conjugate: ConjugateGraphQL | LinkedMoleculeGraphQL
): conjugate is LinkedMoleculeGraphQL {
  return conjugate.__typename === 'LinkedMoleculeNode';
}

export function getMoleculeSideAttachmentText(targetLeavingGroup: number | null): string | null {
  if (targetLeavingGroup != null) {
    return `${LEAVING_GROUP_PREFIX}${targetLeavingGroup}`;
  } else {
    return null;
  }
}

export function getSourceMoleculeSideAttachmentText(
  connectionSide: SourceLinkedMoleculeConnectionSideGraphQL
): string {
  let sourceText = null;
  if (connectionSide.rGroup != null) {
    sourceText = isInaccessible(connectionSide.referencedPolymer.entity)
      ? `No access, ${LEAVING_GROUP_PREFIX}${connectionSide.rGroup}`
      : `${connectionSide.referencedPolymer.entity.name}, ${LEAVING_GROUP_PREFIX}${connectionSide.rGroup}`;
  } else {
    sourceText = isInaccessible(connectionSide.referencedPolymer.entity)
      ? 'No access'
      : connectionSide.referencedPolymer.entity.name;
  }
  return sourceText;
}

function getSourceMoleculeSideAttachmentTextLength(
  connectionSide: SourceLinkedMoleculeConnectionSideGraphQL
): number {
  return getSourceMoleculeSideAttachmentText(connectionSide).length;
}

export function getSourceMonomerSideAttachmentText(
  connectionSide: ConjugateConnectionSideGraphQL
): string | null {
  if (connectionSide.monomerName == null) {
    return null;
  }
  return connectionSide.rGroup != null
    ? `${connectionSide.monomerName}, ${R_GROUP_PREFIX}${connectionSide.rGroup}`
    : `${connectionSide.monomerName}`;
}

function getSourceMonomerSideAttachmentTextLength(connectionSide: ConjugateConnectionSideGraphQL): number {
  const monomerText = getSourceMonomerSideAttachmentText(connectionSide);
  if (monomerText == null) {
    return 0;
  }
  return monomerText.length;
}

export function getConjugatesDiplayWidth(conjugates: Array<ConjugateGraphQL>): number {
  const conjugateDescriptions = conjugates.flatMap((conjugate) => [
    conjugate,
    ...(conjugate.linkedMolecules ?? []),
  ]);

  if (conjugateDescriptions.length === 0) {
    return DEFAULT_TABLE_COLUMN_WIDTH;
  }

  const conjugateDescriptionsWidths = conjugateDescriptions.map((conjugateDescription) => {
    const chipType = new Type(FONT_PROXIMA_13.fontSize);
    const conjugateTextLength = getEntityText(conjugateDescription.entity).length;
    const chipSize = chipType.width(conjugateTextLength) + CHIP_ICON_WIDTH + CHIP_WIDTH_TOTAL_PADDING;

    const descriptionType = new Type(FONT_PROXIMA_14.fontSize);
    let attachmentTextLength = 0;

    if (isLinkedMolecule(conjugateDescription)) {
      const targetSideText = getMoleculeSideAttachmentText(conjugateDescription.connection.target.rGroup);
      attachmentTextLength =
        (targetSideText != null ? targetSideText.length : 0) +
        DASH_WIDTH +
        getSourceMoleculeSideAttachmentTextLength(conjugateDescription.connection.source);
    } else {
      const targetSideText = getMoleculeSideAttachmentText(conjugateDescription.connection.target.rGroup);
      attachmentTextLength =
        (targetSideText != null ? targetSideText.length : 0) +
        DASH_WIDTH +
        getSourceMonomerSideAttachmentTextLength(conjugateDescription.connection.source);
    }

    return (
      chipSize + descriptionType.width(attachmentTextLength) + DASH_WIDTH + TOTAL_HORIZONTAL_CELL_PADDING
    );
  });

  return Math.max(...conjugateDescriptionsWidths);
}
